// hooks/useGlobalHydration.ts — hydrate app-wide social stores once per sign-in.
//
// Mounted in ShellLayout. The topbar unread badges (notifications/messages) and
// the per-post bookmark indicator live in the shell on every in-shell route, so
// these datasets must be hydrated app-wide — not only on their own pages.

import { useEffect, useRef } from 'react';
import { useSession } from '@/hooks';
import { communitiesService } from '@/core/services/communitiesService';
import { messagesService } from '@/core/services/messagesService';
import { notificationsService } from '@/core/services/notificationsService';
import { bookmarksService } from '@/core/services/bookmarksService';
import { followsService } from '@/core/services/followsService';
import { usersService } from '@/core/services/usersService';
import { useHydrationStore, type HydratedResource } from '@/core/stores/hydrationStore';
import {
  connect as connectRealtime,
  disconnect as disconnectRealtime,
  onReconnect,
} from '@/core/lib/realtime';

const LOADERS: Record<HydratedResource, () => Promise<unknown>> = {
  notifications: () => notificationsService.list(),
  conversations: () => messagesService.conversations('all'),
  bookmarks: () => Promise.all([bookmarksService.list(), bookmarksService.folders()]),
};

/**
 * Load one app-wide dataset and record how it went, so its page can show "loading" or
 * "couldn't load, retry" instead of an empty list that looks like "you have none".
 * Also the Retry action those pages call. A refresh of data that is already on screen
 * (`quiet`) never flips the page back to a skeleton.
 */
export function hydrateResource(resource: HydratedResource, quiet = false): Promise<void> {
  const { setStatus } = useHydrationStore.getState();
  if (!quiet) setStatus(resource, 'loading');
  return LOADERS[resource]().then(
    () => setStatus(resource, 'ready'),
    () => {
      if (!quiet) setStatus(resource, 'error');
    },
  );
}

/** True while `resource` has not loaded successfully yet (still loading, or failed). */
export function useHydrationPending(resource: HydratedResource): boolean {
  return useHydrationStore((s) => s.status[resource] !== 'ready');
}

export function useGlobalHydration(): void {
  const { isAuthenticated, currentUser } = useSession();
  const done = useRef(false);

  // Realtime socket lifecycle: open on sign-in, close on sign-out. connect()
  // is itself idempotent (a no-op while already open/connecting), so this
  // effect re-running for unrelated reasons can't double-connect. Deliberately
  // NOT torn down in a cleanup function — ShellLayout can unmount/remount
  // across chrome:'none' routes while still authenticated, and that must not
  // drop the socket. disconnect() runs only in the explicit signed-out branch.
  useEffect(() => {
    if (isAuthenticated) {
      connectRealtime();
    } else {
      disconnectRealtime();
    }
  }, [isAuthenticated]);

  // Catch up after the socket comes back. Anything sent while it was down was never
  // pushed, and the conversation poll stands down whenever the socket is up, so
  // without this a missed notification or message stayed missing until a reload.
  useEffect(() => {
    if (!isAuthenticated) return;
    return onReconnect(() => {
      void hydrateResource('notifications', true);
      void hydrateResource('conversations', true);
    });
  }, [isAuthenticated]);

  // Public dataset — communities are readable signed-out, so a guest sees the real
  // directory instead of the bundled seed. Deliberately keyed on isAuthenticated
  // rather than ref-guarded: the payload carries the viewer's `joined` flag, which
  // is false for everything while anonymous and must be refetched on sign-in.
  useEffect(() => {
    void communitiesService.list().catch(() => {});
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      done.current = false; // re-hydrate on the next sign-in
      return;
    }
    if (done.current) return;
    done.current = true;
    // Follow state drives every Follow button, so it must be app-wide, not page-local.
    void followsService.hydrate().catch(() => {});
    // Pending outgoing follow requests — gates the "Requested" button state.
    void followsService.hydrateRequests().catch(() => {});
    // Mute state gates the ··· menu's Mute/Unmute label — same reload problem as follow.
    void usersService.hydrateMutes().catch(() => {});
    void communitiesService.invites().catch(() => {});
    void hydrateResource('notifications');
    void hydrateResource('conversations');
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    void hydrateResource('bookmarks');
  }, [isAuthenticated, currentUser?.id]);
}
